// src/modules/agentic/MemoryLab.jsx
// Memory Lab — short-term · long-term · episodic · semantic · vector retrieval · RAG memory

import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#a78bfa';
const P  = ['#a78bfa','#22d3ee','#4ade80','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── FAKE EMBEDDING / COSINE SIM ─────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const embed = (text, seed) => {
  // Deterministic fake 8-dim embedding based on text hash
  const h = text.split('').reduce((s,c,i)=>s+c.charCodeAt(0)*(i+1),seed||0);
  return Array.from({length:8},(_,i)=>Math.tanh(Math.sin(h*0.1+i*1.3)*2));
};

const cosine = (a,b) => {
  const dot=a.reduce((s,v,i)=>s+v*b[i],0);
  const na=Math.sqrt(a.reduce((s,v)=>s+v*v,0));
  const nb=Math.sqrt(b.reduce((s,v)=>s+v*v,0));
  return dot/(na*nb+1e-8);
};

// Fake memory store
const MEMORY_DOCS = [
  {id:1, text:'The user prefers concise answers with code examples', category:'preference', time:120},
  {id:2, text:'Project uses React 18 with Vite and TailwindCSS', category:'context', time:240},
  {id:3, text:'User is building an AI education platform called DataDen', category:'context', time:360},
  {id:4, text:'Last conversation: discussed VAE latent space interpolation', category:'episodic', time:480},
  {id:5, text:'User dislikes verbose explanations without visuals', category:'preference', time:600},
  {id:6, text:'Transformer architecture explained: Q K V attention mechanism', category:'knowledge', time:720},
  {id:7, text:'Bug fix: empty list guard in get_max function', category:'episodic', time:840},
  {id:8, text:'User timezone: IST, works late evenings', category:'preference', time:960},
  {id:9, text:'LoRA fine-tuning: rank=8 alpha=16 works best for code tasks', category:'knowledge', time:1080},
  {id:10, text:'Preferred color: dark theme, accent cyan', category:'preference', time:1200},
];

// ─── MEMORY SVG DIAGRAM ───────────────────────────────────────────────────────
function MemoryDiagram({ activeType }) {
  const W=680, H=200;
  const types = [
    {label:'IN-CONTEXT',sub:'context window',color:'#22d3ee',x:60,desc:'~200K tokens. Fast. Vanishes after session.'},
    {label:'EPISODIC',sub:'past events',color:'#4ade80',x:210,desc:'Conversation history, actions taken, outcomes.'},
    {label:'SEMANTIC',sub:'facts & knowledge',color:'#a78bfa',x:360,desc:'Vector DB of facts. Retrieved by similarity.'},
    {label:'PROCEDURAL',sub:'how-to skills',color:'#fbbf24',x:510,desc:'System prompt rules, tool schemas, workflows.'},
  ];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Agent brain center */}
      <ellipse cx={W/2} cy={H/2} rx={40} ry={28} fill="#0f172a" stroke="#334155" strokeWidth={2}/>
      <text x={W/2} y={H/2-4} textAnchor="middle" style={{fontSize:'9px',fill:'#94a3b8',fontFamily:'Syne',fontWeight:800}}>AGENT</text>
      <text x={W/2} y={H/2+8} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>LLM</text>

      {types.map((t,i)=>{
        const active = activeType===t.label.toLowerCase().replace(' ','-');
        return (
          <g key={t.label}>
            <rect x={t.x-55} y={H/2-30} width={110} height={60} rx={5}
              fill={active?t.color+'22':'#0f172a'} stroke={t.color} strokeWidth={active?2:1}/>
            <text x={t.x} y={H/2-12} textAnchor="middle" style={{fontSize:'9px',fill:t.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{t.label}</text>
            <text x={t.x} y={H/2+3} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{t.sub}</text>
            {/* line to center */}
            <line x1={t.x < W/2 ? t.x+55 : t.x-55} y1={H/2}
                  x2={t.x < W/2 ? W/2-40 : W/2+40} y2={H/2}
                  stroke={active?t.color:'#1e293b'} strokeWidth={active?2:1} strokeDasharray={active?'none':'4 2'}/>
            {/* description below */}
            <text x={t.x} y={H/2+46} textAnchor="middle" style={{fontSize:'7px',fill:'#475569',fontFamily:'JetBrains Mono'}}>{t.desc.slice(0,22)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── VECTOR RETRIEVAL VIS ─────────────────────────────────────────────────────
function VectorVis({ docs, query, topK }) {
  if(!query||!docs.length) return null;
  const qEmbed = embed(query, 42);
  const scored = docs.map(d=>({...d, embed:embed(d.text,d.id), sim:0}))
    .map(d=>({...d, sim:+((cosine(qEmbed,d.embed)+1)/2).toFixed(4)}))
    .sort((a,b)=>b.sim-a.sim);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
      {scored.map((d,i)=>(
        <div key={d.id} style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 10px',background:'var(--deep)',borderRadius:'3px',border:`1px solid ${i<topK?P[i%P.length]+'44':'var(--b0)'}`,opacity:i<topK?1:0.45}}>
          <span style={{fontSize:'8px',color:'var(--t3)',minWidth:16,textAlign:'right',fontFamily:'JetBrains Mono'}}>#{d.id}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:'9px',color:i<topK?'var(--t1)':'var(--t3)'}}>{d.text}</div>
            <div style={{fontSize:'8px',color:'#64748b',marginTop:'1px'}}>{d.category}</div>
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'2px'}}>
            <div style={{height:6,width:80,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
              <div style={{height:'100%',width:`${d.sim*100}%`,background:i<topK?P[i%P.length]:'#334155',borderRadius:2}}/>
            </div>
            <span style={{fontSize:'8px',color:i<topK?P[i%P.length]:'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:700}}>{(d.sim*100).toFixed(1)}%</span>
          </div>
          {i<topK&&<span style={{fontSize:'8px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>TOP {i+1}</span>}
        </div>
      ))}
    </div>
  );
}

const TABS = ['types','vector-retrieval','forgetting','memory-ops','architecture'];

export default function MemoryLab() {
  const [tab, setTab] = useState('types');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Memory Lab — how agents remember: context windows, vector DBs, episodic stores.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [activeType, setActiveType] = useState('in-context');
  const [query, setQuery] = useState('What does the user prefer?');
  const [topK, setTopK] = useState(3);
  const [ctxLen, setCtxLen] = useState(8000);
  const [compressionRatio, setCompressionRatio] = useState(0.3);
  const [memStore, setMemStore] = useState(MEMORY_DOCS);
  const [newMemory, setNewMemory] = useState('');
  const [newCategory, setNewCategory] = useState('context');

  const addMemory = () => {
    if(!newMemory.trim()) return;
    const m = {id:memStore.length+1, text:newMemory, category:newCategory, time:Date.now()};
    setMemStore(s=>[...s,m]);
    log(`Memory stored: "${newMemory.slice(0,40)}…" [${newCategory}]`,'ok');
    setNewMemory('');
  };

  // Forgetting curve simulation
  const forgettingData = Array.from({length:50},(_,i)=>({
    t:i,
    noMemory: +(Math.exp(-i*0.15)*100).toFixed(1),
    summarize: +(Math.exp(-i*0.04)*100).toFixed(1),
    vectorDB:  +(95-i*0.3).toFixed(1),
    fullHistory: +(100-i*1.2).toFixed(1),
  }));

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── TYPES ── */}
      {tab==='types'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="4 TYPES OF AGENT MEMORY" accent={AC}>
            <MemoryDiagram activeType={activeType}/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',marginTop:'10px'}}>
              {[
                {key:'in-context',label:'IN-CONTEXT',color:'#22d3ee',capacity:'~200K tok',speed:'Instant',persist:'Session only'},
                {key:'episodic',label:'EPISODIC',color:'#4ade80',capacity:'Unlimited',speed:'Fast',persist:'Persistent'},
                {key:'semantic',label:'SEMANTIC',color:'#a78bfa',capacity:'Millions',speed:'~10ms',persist:'Persistent'},
                {key:'procedural',label:'PROCEDURAL',color:'#fbbf24',capacity:'Fixed',speed:'Instant',persist:'Permanent'},
              ].map(t=>(
                <button key={t.key} onClick={()=>setActiveType(t.key)} style={{padding:'10px',background:activeType===t.key?t.color+'18':'var(--deep)',border:`1px solid ${activeType===t.key?t.color:'var(--b0)'}`,borderRadius:'3px',cursor:'pointer',textAlign:'left'}}>
                  <div style={{fontSize:'9px',color:t.color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'5px'}}>{t.label}</div>
                  <div style={{fontSize:'8px',color:'var(--t3)',lineHeight:1.6}}>Cap: {t.capacity}<br/>Speed: {t.speed}<br/>Persist: {t.persist}</div>
                </button>
              ))}
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="IN-CONTEXT vs EXTERNAL MEMORY" accent="#22d3ee">
              {[
                {aspect:'Access',inCtx:'Read directly in prompt',external:'Retrieve by query/key'},
                {aspect:'Capacity',inCtx:'Limited by model window',external:'Unlimited (DB scales)'},
                {aspect:'Precision',inCtx:'Perfect (exact tokens)',external:'Approximate (similarity)'},
                {aspect:'Speed',inCtx:'Zero latency',external:'10-100ms per query'},
                {aspect:'Cost',inCtx:'Tokens = $ per request',external:'Storage + query cost'},
                {aspect:'Persistence',inCtx:'Dies with session',external:'Lives forever'},
              ].map(r=>(
                <div key={r.aspect} style={{display:'grid',gridTemplateColumns:'80px 1fr 1fr',gap:'6px',padding:'5px',borderBottom:'1px solid var(--b0)',fontSize:'9px'}}>
                  <span style={{color:'var(--t3)',fontWeight:700}}>{r.aspect}</span>
                  <span style={{color:'#22d3ee'}}>{r.inCtx}</span>
                  <span style={{color:'#a78bfa'}}>{r.external}</span>
                </div>
              ))}
            </Panel>
            <Panel title="MEMORY TYPES IN PRODUCTION AGENTS" accent="#4ade80">
              {[
                {system:'GPT-4 Assistants',mem:'In-context + File store',color:'#fbbf24'},
                {system:'LangChain Agent',mem:'ConversationBuffer + VectorDB',color:'#22d3ee'},
                {system:'AutoGPT',mem:'Context + local JSON files',color:'#4ade80'},
                {system:'MemGPT',mem:'Multi-tier: main, FIFO, archival',color:'#a78bfa'},
                {system:'Claude Projects',mem:'In-context + project knowledge',color:'#f87171'},
                {system:'Cursor (AI IDE)',mem:'Codebase index + conversation',color:'#fb923c'},
              ].map(s=>(
                <div key={s.system} style={{padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px',borderLeft:`2px solid ${s.color}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:'9px',color:s.color,fontWeight:700}}>{s.system}</span>
                  <span style={{fontSize:'8px',color:'var(--t3)'}}>{s.mem}</span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {/* ── VECTOR RETRIEVAL ── */}
      {tab==='vector-retrieval'&&(
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="RETRIEVAL CONFIG" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.1em'}}>QUERY</div>
              <input value={query} onChange={e=>setQuery(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <Sl label="Top K results" value={topK} min={1} max={8} step={1} onChange={setTopK} color={AC}/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',marginTop:'4px'}}>
                <Metric label="Memory items" value={memStore.length} color={AC}/>
                <Metric label="Top K" value={topK} color="#4ade80"/>
              </div>
            </Panel>
            <Panel title="ADD MEMORY" accent="#4ade80">
              <textarea value={newMemory} onChange={e=>setNewMemory(e.target.value)} placeholder="New memory to store..."
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'10px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'8px',boxSizing:'border-box',resize:'none',height:60}}/>
              <select value={newCategory} onChange={e=>setNewCategory(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px',fontSize:'10px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none',marginBottom:'8px'}}>
                {['preference','context','episodic','knowledge'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <Btn onClick={addMemory} color="#4ade80" style={{width:'100%'}}>STORE →</Btn>
            </Panel>
            <LogPanel logs={logs} height={100}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title={`COSINE SIMILARITY RETRIEVAL — "${query.slice(0,40)}"`} accent={AC}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Docs ranked by cosine similarity to query embedding. Top {topK} retrieved and injected into context.</div>
              <VectorVis docs={memStore} query={query} topK={topK}/>
            </Panel>
          </div>
        </div>
      )}

      {/* ── FORGETTING ── */}
      {tab==='forgetting'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="AGENT MEMORY RETENTION OVER TIME — STRATEGY COMPARISON" accent="#f87171">
            <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>How much of a 50-turn conversation does the agent remember, by memory strategy?</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={forgettingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'conversation turns',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,100]} label={{value:'% retained',fill:'var(--t3)',fontSize:9,angle:-90,position:'insideLeft'}}/>
                <Tooltip/><Legend/>
                <Line type="monotone" dataKey="noMemory"   stroke="#f87171" strokeWidth={2} dot={false} name="No memory"/>
                <Line type="monotone" dataKey="summarize"  stroke="#fbbf24" strokeWidth={2} dot={false} name="Rolling summary"/>
                <Line type="monotone" dataKey="vectorDB"   stroke="#a78bfa" strokeWidth={2} dot={false} name="Vector DB"/>
                <Line type="monotone" dataKey="fullHistory" stroke="#4ade80" strokeWidth={2} dot={false} strokeDasharray="5 3" name="Full history (expensive)"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
            {[
              {name:'No Memory',color:'#f87171',ret:'~0%',cost:'Lowest',best:'Stateless APIs'},
              {name:'Summarize',color:'#fbbf24',ret:'~60%',cost:'Low',best:'Chat assistants'},
              {name:'Vector DB',color:'#a78bfa',ret:'~95%',cost:'Medium',best:'Long-term agents'},
              {name:'Full History',color:'#4ade80',ret:'100%',cost:'High',best:'Short tasks only'},
            ].map(s=>(
              <Panel key={s.name} title={s.name} accent={s.color}>
                <div style={{fontSize:'9px'}}><span style={{color:'var(--t3)'}}>Retention: </span><span style={{color:s.color,fontWeight:700}}>{s.ret}</span></div>
                <div style={{fontSize:'9px',marginTop:'3px'}}><span style={{color:'var(--t3)'}}>Cost: </span><span style={{color:'var(--t2)'}}>{s.cost}</span></div>
                <div style={{fontSize:'9px',marginTop:'3px'}}><span style={{color:'var(--t3)'}}>Best for: </span><span style={{color:'var(--t2)'}}>{s.best}</span></div>
              </Panel>
            ))}
          </div>
        </div>
      )}

      {/* ── MEMORY OPS ── */}
      {tab==='memory-ops'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
          {[
            {op:'WRITE',icon:'✍',color:'#4ade80',when:'After each significant event or fact',how:'Summarize → embed → upsert into vector DB',code:'memory.write("user prefers Python", tags=["pref"])'},
            {op:'READ',icon:'📖',color:'#22d3ee',when:'Before each reasoning step needing context',how:'Embed query → ANN search → inject top-K into prompt',code:'memory.search("user preference", k=3)'},
            {op:'UPDATE',icon:'🔄',color:'#fbbf24',when:'When existing memory becomes outdated',how:'Delete old embedding, insert new version',code:'memory.update(id=42, text="updated pref")'},
            {op:'DELETE',icon:'🗑',color:'#f87171',when:'Privacy, stale data, or memory overflow',how:'Remove by ID or TTL-based expiry',code:'memory.delete(id=42) / memory.expire(ttl=7d)'},
            {op:'COMPRESS',icon:'🗜',color:'#a78bfa',when:'Context window filling up mid-task',how:'LLM summarizes old turns → replace with summary',code:'memory.compress(turns[:50], ratio=0.2)'},
            {op:'REFLECT',icon:'🤔',color:'#fb923c',when:'End of task or periodically',how:'LLM reviews memories → extract insights → store',code:'memory.reflect() → new_facts extracted'},
          ].map(op=>(
            <Panel key={op.op} title={`${op.icon} ${op.op}`} accent={op.color}>
              <div style={{fontSize:'9px',marginBottom:'4px'}}><span style={{color:'var(--t3)'}}>When: </span><span style={{color:'var(--t2)'}}>{op.when}</span></div>
              <div style={{fontSize:'9px',marginBottom:'6px'}}><span style={{color:'var(--t3)'}}>How: </span><span style={{color:'var(--t2)'}}>{op.how}</span></div>
              <div style={{padding:'6px 8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'9px',color:op.color}}>{op.code}</div>
            </Panel>
          ))}
        </div>
      )}

      {/* ── ARCHITECTURE ── */}
      {tab==='architecture'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="CONTEXT WINDOW USAGE — WHAT FILLS IT" accent={AC}>
            <Sl label="Total context tokens" value={ctxLen} min={1000} max={200000} step={1000} onChange={setCtxLen} color={AC}/>
            <Sl label="Compression ratio" value={compressionRatio} min={0.05} max={0.9} step={0.05} onChange={setCompressionRatio} color="#4ade80"/>
            <div style={{display:'flex',gap:'3px',marginTop:'8px',marginBottom:'8px',height:40}}>
              {[
                {label:'System',pct:8,color:'#a78bfa'},
                {label:'Tools',pct:12,color:'#fbbf24'},
                {label:'Retrieved Memory',pct:18,color:'#4ade80'},
                {label:'Task/User',pct:15,color:'#22d3ee'},
                {label:'Prev turns',pct:30,color:'#fb923c'},
                {label:'Free',pct:17,color:'#1e293b'},
              ].map(s=>(
                <div key={s.label} title={`${s.label}: ${s.pct}%`} style={{flex:s.pct,background:s.color+'44',border:`1px solid ${s.color}`,borderRadius:'2px',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                  <span style={{fontSize:'7px',color:s.color,fontFamily:'JetBrains Mono',fontWeight:700,whiteSpace:'nowrap'}}>{s.pct}%</span>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
              <Metric label="Total tokens" value={ctxLen.toLocaleString()} color={AC}/>
              <Metric label="After compress" value={Math.round(ctxLen*compressionRatio).toLocaleString()} color="#4ade80"/>
              <Metric label="Cost/1M (GPT-4)" value={`$${(ctxLen/1000000*15).toFixed(4)}`} color="#f87171"/>
            </div>
          </Panel>
          <Panel title="MemGPT / VIRTUAL CONTEXT — PAGING MEMORY LIKE AN OS" accent="#4ade80">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {tier:'MAIN CTX',desc:'Current working memory. Fits in context window. Instant access.',capacity:'~4K tokens',speed:'0ms',color:'#22d3ee'},
                {tier:'FIFO QUEUE',desc:'Recent history queue. Oldest messages evicted when main ctx full.',capacity:'~16K tokens',speed:'<1ms',color:'#4ade80'},
                {tier:'ARCHIVAL',desc:'Full history, vector indexed. Retrieved by semantic search on demand.',capacity:'Unlimited',speed:'~10ms',color:'#a78bfa'},
              ].map(t=>(
                <div key={t.tier} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${t.color}`}}>
                  <div style={{fontSize:'10px',color:t.color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'5px'}}>{t.tier}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{t.desc}</div>
                  <div style={{fontSize:'9px'}}><span style={{color:'var(--t3)'}}>Size: </span><span style={{color:t.color}}>{t.capacity}</span></div>
                  <div style={{fontSize:'9px',marginTop:'2px'}}><span style={{color:'var(--t3)'}}>Access: </span><span style={{color:'var(--t2)'}}>{t.speed}</span></div>
                </div>
              ))}
            </div>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
